import {useMemo} from 'react';
import {useParams} from 'react-router';

import {RunInfo, useRunComparerQuery} from '../../../generated/graphql';
import type {RunKeyInfo} from '../../../types/run';
import {KeyVal} from '../../../util/runTypes';
import {displayTimeDifference} from '../../elements/TimeDisplay';
import type {Tree, TreeLeaf} from '../types';
import {getKeyNameParts} from './getKeyNameParts';
import {getTruncatedTree} from './getTruncatedTree';
import {isBoringArray} from './isBoringArray';
import {isNilArray} from './isNilArray';
import {setNestedProperty} from './setNestedProperty';

type RunComparerRun = {
  name: string;
  createdAt: string;
  heartbeatAt: string;
  notes: string;
  config: KeyVal;
  summary: KeyVal;
};

export type UseRunComparerTreeArgs = {
  loading: boolean;
  runs: RunComparerRun[];
  keyInfo: RunKeyInfo;
  diffOnly?: boolean;
  searchString?: string;
};

function safeRegexMatch(searchString: string, value: string) {
  try {
    return value.match(searchString) != null;
  } catch (e) {
    return false;
  }
}

const META_KEYS = [
  'runtime',
  'notes',
  'program',
  'args',
  'os',
  'python',
  'colab',
  'executable',
  'codeSaved',
  'cpuCount',
  'cpuCountLogical',
  'gpuCount',
  'gpu',
  'git',
  'codePath',
  'slurm',
] as const;
type MetaTreeKey = (typeof META_KEYS)[number];
type MetaTree = {
  [key in MetaTreeKey]?: Tree | TreeLeaf;
};

function getMetaTreeForKey(
  runs: RunComparerRun[],
  key: MetaTreeKey,
  runNameToRunInfoMap: Partial<Record<string, RunInfo | null | undefined>>
): Tree | TreeLeaf {
  switch (key) {
    case 'runtime':
      return runs.map(({createdAt, heartbeatAt}) =>
        displayTimeDifference(
          new Date(createdAt),
          new Date(heartbeatAt),
          'month_round'
        )
      );
    case 'notes':
      return runs.map(({notes}) => notes);
    case 'git':
      return runs.map(({name}) =>
        runNameToRunInfoMap?.[name]?.git
          ? JSON.stringify(runNameToRunInfoMap?.[name]?.git)
          : ''
      );
    case 'codePath':
      return runs.map(({name}) =>
        runNameToRunInfoMap?.[name]?.codePath
          ? JSON.stringify(runNameToRunInfoMap?.[name]?.codePath)
          : ''
      );
    case 'slurm':
      return runs.map(({name}) =>
        runNameToRunInfoMap?.[name]?.slurm
          ? JSON.stringify(runNameToRunInfoMap?.[name]?.slurm)
          : ''
      );
    default:
      return runs.map(({name}) => runNameToRunInfoMap?.[name]?.[key] ?? '');
  }
}

function isWandbConfig(keyName: string) {
  return keyName === '_wandb';
}

/**
 * Builds a tree of run data for display in the run comparer using keyInfo.
 *
 * @param {UseRunComparerTreeArgs} args
 * @param {number} [limit=2000]
 * @returns {Tree}
 */
export function useRunComparerTree(
  {
    loading,
    runs,
    keyInfo,
    diffOnly = false,
    searchString,
  }: UseRunComparerTreeArgs,
  limit?: number
): {tree: Tree; loading: boolean} {
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  const {data, loading: runInfoLoading} = useRunComparerQuery({
    variables: {
      projectName,
      entityName,
      runNames: runs.map(run => run.name),
    },
  });
  const runNameToRunInfoMap = useMemo(() => {
    return (
      data?.project?.runs?.edges?.reduce(
        (acc, edge) => {
          acc[edge.node.name] = edge.node.runInfo;
          return acc;
        },
        {} as Partial<Record<string, RunInfo | null | undefined>>
      ) ?? {}
    );
  }, [data]);
  return useMemo(() => {
    if (loading || runInfoLoading || !keyInfo) {
      return {tree: {}, loading: true};
    }

    // Add meta data directly to the tree if it matches the search string
    const metaTree: MetaTree = {};
    META_KEYS.forEach(metaKey => {
      const includeKey =
        searchString == null || safeRegexMatch(searchString, metaKey);
      const treeForKey = includeKey
        ? getMetaTreeForKey(runs, metaKey, runNameToRunInfoMap)
        : undefined;
      if (treeForKey) {
        if (Array.isArray(treeForKey)) {
          if (
            !isNilArray(treeForKey) &&
            !(diffOnly && runs.length > 1 && isBoringArray(treeForKey))
          ) {
            metaTree[metaKey] = treeForKey;
          }
          // Ignore the key if it's an empty array or if diff only mode is on and there's no different values
        } else {
          // If it's not an array, then add it to the tree no matter what as a sub tree
          metaTree[metaKey] = treeForKey;
        }
      }
    });
    const includeMeta =
      Object.keys(metaTree).length > 0 ||
      searchString == null ||
      safeRegexMatch(searchString, 'meta');

    const tree: Tree = {};
    if (includeMeta) {
      tree.meta = metaTree;
    }

    // Iterate over each key in keyInfo to build the unflattened tree directly
    Object.keys(keyInfo).forEach(key => {
      const colonIndex = key.indexOf(':');
      const keyType = key.substring(0, colonIndex);
      const keyName = key.substring(colonIndex + 1);

      const value: TreeLeaf = [];

      if (keyType === 'config') {
        runs.forEach(run => {
          value.push(run.config[keyName]);
        });
        // Remove '.value' suffix if present
        const cleanKeyName = keyName.replace('.value', '');
        // Split by '/' and '.' (not before a number) to handle nested properties
        const path = ['config', ...getKeyNameParts(cleanKeyName)];

        // Check if the value should be excluded
        if (
          !isWandbConfig(keyName) &&
          !isNilArray(value) &&
          !(diffOnly && runs.length > 1 && isBoringArray(value)) &&
          (searchString == null ||
            path.some(part => safeRegexMatch(searchString, part))) // exclude it if no part of the path matches the search string
        ) {
          setNestedProperty(tree, path, value);
        }
      } else {
        let isHistogram = false;
        runs.forEach(run => {
          const runVal = run.summary[keyName];
          value.push(runVal);

          // The majorType for histograms is number and MediaType doesn't include
          // histograms, so this is the only way to tell that this is a histogram.
          // @ts-expect-error
          if (runVal != null && runVal._type === 'histogram') {
            isHistogram = true;
          }
        });

        // Split by '/' and '.' (not before a number) to handle nested properties
        const path = [
          isHistogram ? 'histograms' : 'summary',
          ...getKeyNameParts(keyName),
        ];

        // Check if the value should be excluded
        if (
          !isHistogram && // Exclude histograms from the tree because they are causing crashes in the run comparer
          !isNilArray(value) &&
          !(diffOnly && runs.length > 1 && isBoringArray(value)) &&
          (searchString == null ||
            path.some(part => safeRegexMatch(searchString, part))) // exclude it if no part of the path matches the search string
        ) {
          setNestedProperty(tree, path, value);
        }
      }
    });

    // Truncate the tree to help with non-virtualized performance
    if (limit) {
      const truncatedTree: Tree = {};
      Object.keys(tree).forEach(key => {
        const value = tree[key];
        truncatedTree[key] = getTruncatedTree(value, limit);
      });
      return {tree: truncatedTree, loading: false};
    }

    return {tree, loading: false};
  }, [
    loading,
    runInfoLoading,
    runs,
    keyInfo,
    diffOnly,
    limit,
    searchString,
    runNameToRunInfoMap,
  ]);
}
